import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import permissionManager from '../utils/permissionManager';
import apiService from '../services/apiService';

const Sidebar = () => {
  const sidebarToggleRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isManagementOpen, setIsManagementOpen] = useState(false);

  // Load user permissions on component mount
  useEffect(() => {
    const permissions = permissionManager.getPermissions();
    const role = permissionManager.getRoleName();
    setUserPermissions(permissions);
    setUserRole(role);
  }, []);

  // Setup dashboard WebSocket for real-time updates
  useEffect(() => {
    const cleanupWebSocket = apiService.setupDashboardWebSocket((data) => {
      console.log('Dashboard WebSocket update received from Sidebar:', data);
      // Dispatch custom event to notify Dashboard component
      window.dispatchEvent(new CustomEvent('dashboardUpdate', { detail: data }));
    });

    // Cleanup WebSocket on component unmount
    return () => {
      if (cleanupWebSocket) {
        cleanupWebSocket();
      }
    };
  }, []);

  // Listen for notification count updates
  useEffect(() => {
    const handleNotificationUpdate = (event) => {
      if (event.detail && event.detail.unread_count !== undefined) {
        setNotificationCount(event.detail.unread_count);
      }
    };

    window.addEventListener('notificationCountUpdate', handleNotificationUpdate);

    // Initial fetch of notification count
    const fetchInitialNotificationCount = async () => {
      try {
        const data = await apiService.getUnreadNotificationsCount();
        setNotificationCount(data.unread_count || 0);
      } catch (error) {
        console.error('Error fetching initial notification count:', error);
      }
    };

    fetchInitialNotificationCount();

    return () => {
      window.removeEventListener('notificationCountUpdate', handleNotificationUpdate);
    };
  }, []);

  useEffect(() => {
    // Ensure jQuery is available
    if (window.jQuery) {
      // Initialize sidebar toggle manually
      window.jQuery(sidebarToggleRef.current).on('click', function() {
        window.jQuery("body").toggleClass("sidebar-toggled");
        window.jQuery(".sidebar").toggleClass("toggled");
        if (window.jQuery(".sidebar").hasClass("toggled")) {
          window.jQuery('.sidebar .collapse').collapse('hide');
        }
      });
    }
  }, []);

  // Check if current route is related to automation
  const automationRoutes = [
    '/dashboard',
    '/automation-runs', 
    '/automation/logs', 
    '/automation',
    '/automation/both-agents-present',
    '/automation/listing-agents-only',
    '/automation/selling-agents-only',
    '/automation/fsbo-no-agents',
    '/automation/agent-copy-missing',
    '/automation/both-agents-missing',
    '/automation/no-completion-status',
    '/automation/failed',
    '/closing-of-the-day'
  ];
  const isAutomationRoute = automationRoutes.some(route => location.pathname === route || location.pathname.startsWith(route + '/'));
  const managementRoutes = ['/manage/salespersons', '/manage/transaction-coordinators', '/manage/processors', '/manage/managers'];
  const isManagementRoute = managementRoutes.some(route => location.pathname === route || location.pathname.startsWith(route + '/'));
  const commissionsRoutes = ['/salespersons', '/commissions'];
  const isCommissionsRoute = commissionsRoutes.some(route => location.pathname === route || location.pathname.startsWith(route + '/'));
  
  // Auto-open dropdown if on automation-related page, close otherwise
  useEffect(() => {
    if (isAutomationRoute) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  }, [location.pathname, isAutomationRoute]);

  // Auto-open Production Team dropdown if on management-related page
  useEffect(() => {
    if (isManagementRoute) {
      setIsManagementOpen(true);
    } else {
      setIsManagementOpen(false);
    }
  }, [location.pathname, isManagementRoute]);

  // Commissions dropdown state
  const [isCommissionsOpen, setIsCommissionsOpen] = useState(false);
  useEffect(() => {
    if (isCommissionsRoute) {
      setIsCommissionsOpen(true);
    } else {
      setIsCommissionsOpen(false);
    }
  }, [location.pathname, isCommissionsRoute]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };
  const toggleManagementDropdown = () => {
    setIsManagementOpen(!isManagementOpen);
  };
  const toggleCommissionsDropdown = () => {
    setIsCommissionsOpen(!isCommissionsOpen);
  };

  return (
    <>
    <style jsx>{`
                
                
            `}</style>
    <ul className="navbar-nav sidebar sidebar-light accordion" id="accordionSidebar" style={{
      backgroundColor: 'white',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      borderRight: '1px solid #e9ecef'
    }}>
      {/* Sidebar - Brand */}
      <Link className="sidebar-brand d-flex align-items-center justify-content-center" to="/">
        <div className="sidebar-brand-icon">
          <img src="/logo.webp" alt="Spire Abstract" style={{ width: '52%', objectFit: 'contain' }} />
        </div>
        
      </Link>

      {/* Divider */}
      <hr className="sidebar-divider my-0" style={{ borderColor: '#e9ecef' }} />

      {/* Automation Section - Only show if user has automation permissions */}
      {(permissionManager.hasPermission('view_automation_runs') || permissionManager.hasPermission('manage_automation_runs')) && (
        <>
          {/* Divider */}
          <hr className="sidebar-divider" style={{ borderColor: '#e9ecef' }} />

          {/* Heading */}
          <div className="sidebar-heading" style={{ color: '#495057', fontWeight:"bold", fontSize:"0.7rem", textTransform: 'uppercase', letterSpacing: '0.5px'}}>Operations</div>



          {/* Nav Item - Automation Trigger (Dropdown) */}
          <li className={`nav-item ${isAutomationRoute ? 'active' : ''}`}>
        <a 
          className={`nav-link`} 
          href="#" 
          onClick={(e) => {
            e.preventDefault();
            toggleDropdown();
          }}
          data-target="#collapseAutomation"
          aria-expanded={isDropdownOpen}
          aria-controls="collapseAutomation"
          style={{
            color: isAutomationRoute ? '#667eea' : '#495057',
            backgroundColor: isAutomationRoute ? '#f8f9fa' : 'transparent',
            // borderRadius: '8px',
            // margin: '0 0.5rem',
            fontWeight: isAutomationRoute ? '600' : '500',
            transition: 'all 0.3s ease'
          }}
        >
          <i className="fas fa-fw fa-play-circle" style={{ marginRight: '0.5rem' }}></i>
          <span>Agent Copies</span>
          {notificationCount > 0 && (
            <span className="badge badge-success badge-counters ml-2" style={{padding:"0.1em 0.4em", borderRadius:"10px"}}>{notificationCount}</span>
          )} &nbsp; 
          <i className={`fas fa-chevron-down ms-auto transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} style={{
            transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease'
          }}></i>
        </a>
        <div 
          id="collapseAutomation" 
          className={`collapse ${isDropdownOpen ? 'show' : ''}`}
          aria-labelledby="headingAutomation" 
          data-parent="#accordionSidebar"
        >
          <div className="py-2 collapse-inner rounded" style={{
            // backgroundColor: '#f8f9fa',
            borderLeft: '3px solid #e9ecef',
            borderLeftStyle:"double"
            // marginLeft: '1rem',
          }}>
            {permissionManager.hasPermission('view_dashboard') && (
              <Link 
                className={`collapse-item ${location.pathname === '/dashboard' ? 'active' : ''}`} 
                to="/dashboard"
                onClick={(e) => {
                  e.preventDefault();
                  setIsDropdownOpen(false);
                  
                  if (location.pathname !== '/dashboard') {
                    setTimeout(() => {
                      navigate('/dashboard');
                    }, 150);
                  }
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== '/dashboard') {
                    e.target.style.backgroundColor = '#e9ecef';
                    e.target.style.color = '#667eea';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== '/dashboard') {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#495057';
                  }
                }}
                style={{
                  color: location.pathname === '/dashboard' ? '#667eea' : '#495057',
                  backgroundColor: location.pathname === '/dashboard' ? 'white' : 'transparent',
                  borderRadius: '6px',
                  padding: '0.5rem 0.75rem',
                  margin: '0.25rem 0',
                  fontWeight: location.pathname === '/dashboard' ? '600' : '500',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  display: 'block',
                  cursor: 'pointer'
                }}
              >
                Analytics
              </Link>
            )}
            
            <div className="dropdown-divider" style={{ borderColor: '#dee2e6' }}></div>
            
            {permissionManager.hasPermission('view_automation_runs') && (
              <Link 
                className={`collapse-item ${location.pathname === '/automation-runs' ? 'active' : ''}`} 
                to="/automation-runs"
                onClick={(e) => {
                  e.preventDefault();
                  setIsDropdownOpen(false);
                  
                  if (location.pathname !== '/automation-runs') {
                    setTimeout(() => {
                      navigate('/automation-runs');
                    }, 150);
                  }
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== '/automation-runs') {
                    e.target.style.backgroundColor = '#e9ecef';
                    e.target.style.color = '#667eea';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== '/automation-runs') {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#495057';
                  }
                }}
                style={{
                  color: location.pathname === '/automation-runs' ? '#667eea' : '#495057',
                  backgroundColor: location.pathname === '/automation-runs' ? 'white' : 'transparent',
                  borderRadius: '6px',
                  padding: '0.5rem 0.75rem',
                  margin: '0.25rem 0',
                  fontWeight: location.pathname === '/automation-runs' ? '600' : '500',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  display: 'block',
                  cursor: 'pointer'
                }}
              >
                Closing Coordinator
              </Link>
            )}
            
            <div className="dropdown-divider" style={{ borderColor: '#dee2e6' }}></div>
            
            {/* <Link 
              className={`collapse-item ${location.pathname === '/automation/failed' ? 'active' : ''}`} 
              to="/automation/failed"
              onClick={(e) => {
                e.preventDefault();
                setIsDropdownOpen(false);
                setTimeout(() => {
                  navigate('/automation/failed');
                }, 150);
              }}
              onMouseEnter={(e) => {
                if (location.pathname !== '/automation/failed') {
                  e.target.style.backgroundColor = '#e9ecef';
                  e.target.style.color = '#667eea';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== '/automation/failed') {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#495057';
                }
              }}
              style={{
                color: location.pathname === '/automation/failed' ? '#667eea' : '#495057',
                backgroundColor: location.pathname === '/automation/failed' ? 'white' : 'transparent',
                borderRadius: '6px',
                padding: '0.5rem 0.75rem',
                margin: '0.25rem 0',
                fontWeight: location.pathname === '/automation/failed' ? '600' : '500',
                transition: 'all 0.3s ease',
                textDecoration: 'none',
                display: 'block',
                cursor: 'pointer'
              }}
            >
              Failed Closing Coordinator
            </Link> */}
            
            <div className="dropdown-divider" style={{ borderColor: '#dee2e6' }}></div>
            
            {permissionManager.hasPermission('view_dashboard') && (
              <Link 
                className={`collapse-item ${location.pathname === '/closing-of-the-day' ? 'active' : ''}`} 
                to="/closing-of-the-day"
                onClick={(e) => {
                  e.preventDefault();
                  setIsDropdownOpen(false);
                  setTimeout(() => {
                    navigate('/closing-of-the-day');
                  }, 150);
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== '/closing-of-the-day') {
                    e.target.style.backgroundColor = '#e9ecef';
                    e.target.style.color = '#667eea';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== '/closing-of-the-day') {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#495057';
                  }
                }}
                style={{
                  color: location.pathname === '/closing-of-the-day' ? '#667eea' : '#495057',
                  backgroundColor: location.pathname === '/closing-of-the-day' ? 'white' : 'transparent',
                  borderRadius: '6px',
                  padding: '0.5rem 0.75rem',
                  margin: '0.25rem 0',
                  fontWeight: location.pathname === '/closing-of-the-day' ? '600' : '500',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  display: 'block',
                  cursor: 'pointer'
                }}
              >
                Expected Closings
              </Link>
            )}
            

            
          </div>
        </div>
      </li>
        
          {/* Nav Item - Scheduled Appointments */}
          {permissionManager.hasPermission('view_dashboard') && (
            <li className={`nav-item ${location.pathname === '/scheduled-appointments' ? 'active' : ''}`}>
              <Link className="nav-link" to="/scheduled-appointments" style={{
                color: location.pathname === '/scheduled-appointments' ? '#667eea' : '#495057',
                backgroundColor: location.pathname === '/scheduled-appointments' ? '#f8f9fa' : 'transparent',
                fontWeight: location.pathname === '/scheduled-appointments' ? '600' : '500',
                transition: 'all 0.3s ease'
              }}>
                <i className="fas fa-fw fa-calendar-alt" style={{ marginRight: '0.5rem' }}></i>
                <span>Scheduled Appointments</span>
              </Link>
            </li>
          )}
          

        </>
      )}
{/* Nav Item - Closing of the Day */}
      <hr className="sidebar-divider" style={{ borderColor: '#e9ecef' }} />
              <div className="sidebar-heading" style={{ color: '#495057', fontWeight:"bold", fontSize:"0.7rem", textTransform: 'uppercase', letterSpacing: '0.5px'}}>Commissions</div>

      {/* Users Production Team Section - Only for super_admin and manager */}
      {permissionManager.hasPermission('view_dashboard') && (process.env.REACT_APP_API_BASE_URL !== 'https://backend.spireabstract.com/api')  && (
        <>
          
          <li className={`nav-item ${isManagementRoute ? 'active' : ''}`}>
            <a 
              className={`nav-link`} 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                toggleManagementDropdown();
              }}
              data-target="#collapseManagement"
              aria-expanded={isManagementOpen}
              aria-controls="collapseManagement"
              style={{
                color: isManagementRoute ? '#667eea' : '#495057',
                backgroundColor: isManagementRoute ? '#f8f9fa' : 'transparent',
                fontWeight: isManagementRoute ? '600' : '500',
                transition: 'all 0.3s ease'
              }}
            >
              <i className="fas fa-fw fa-layer-group" style={{ marginRight: '0.5rem' }}></i>
              <span>Production & Sales Team  &nbsp;&nbsp;</span>
              <i className={`ml-2 fas fa-chevron-down ms-auto transition-transform ${isManagementOpen ? 'rotate-180' : ''}`} style={{
                transform: isManagementOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease'
              }}></i>
            </a>
            <div 
              id="collapseManagement" 
              className={`collapse ${isManagementOpen ? 'show' : ''}`}
              aria-labelledby="headingManagement" 
              data-parent="#accordionSidebar"
            >
              <div className="py-2 collapse-inner rounded" style={{
                borderLeft: '3px solid #e9ecef',
                borderLeftStyle:"double"
              }}>
                <Link 
                  className={`collapse-item ${location.pathname === '/manage/salespersons' ? 'active' : ''}`} 
                  to="/manage/salespersons"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsManagementOpen(false);
                    if (location.pathname !== '/manage/salespersons') {
                      setTimeout(() => { navigate('/manage/salespersons'); }, 150);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== '/manage/salespersons') {
                      e.target.style.backgroundColor = '#e9ecef';
                      e.target.style.color = '#667eea';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== '/manage/salespersons') {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#495057';
                    }
                  }}
                  style={{
                    color: location.pathname === '/manage/salespersons' ? '#667eea' : '#495057',
                    backgroundColor: location.pathname === '/manage/salespersons' ? 'white' : 'transparent',
                    borderRadius: '6px',
                    padding: '0.5rem 0.75rem',
                    margin: '0.25rem 0',
                    fontWeight: location.pathname === '/manage/salespersons' ? '600' : '500',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none',
                    display: 'block',
                    cursor: 'pointer'
                  }}
                >
                  Sales persons
                </Link>

                <div className="dropdown-divider" style={{ borderColor: '#dee2e6' }}></div>

                <Link 
                  className={`collapse-item ${location.pathname === '/manage/transaction-coordinators' ? 'active' : ''}`} 
                  to="/manage/transaction-coordinators"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsManagementOpen(false);
                    if (location.pathname !== '/manage/transaction-coordinators') {
                      setTimeout(() => { navigate('/manage/transaction-coordinators'); }, 150);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== '/manage/transaction-coordinators') {
                      e.target.style.backgroundColor = '#e9ecef';
                      e.target.style.color = '#667eea';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== '/manage/transaction-coordinators') {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#495057';
                    }
                  }}
                  style={{
                    color: location.pathname === '/manage/transaction-coordinators' ? '#667eea' : '#495057',
                    backgroundColor: location.pathname === '/manage/transaction-coordinators' ? 'white' : 'transparent',
                    borderRadius: '6px',
                    padding: '0.5rem 0.75rem',
                    margin: '0.25rem 0',
                    fontWeight: location.pathname === '/manage/transaction-coordinators' ? '600' : '500',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none',
                    display: 'block',
                    cursor: 'pointer'
                  }}
                >
                  Coordinators
                </Link>

                <div className="dropdown-divider" style={{ borderColor: '#dee2e6' }}></div>

                <Link 
                  className={`collapse-item ${location.pathname === '/manage/processors' ? 'active' : ''}`} 
                  to="/manage/processors"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsManagementOpen(false);
                    if (location.pathname !== '/manage/processors') {
                      setTimeout(() => { navigate('/manage/processors'); }, 150);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== '/manage/processors') {
                      e.target.style.backgroundColor = '#e9ecef';
                      e.target.style.color = '#667eea';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== '/manage/processors') {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#495057';
                    }
                  }}
                  style={{
                    color: location.pathname === '/manage/processors' ? '#667eea' : '#495057',
                    backgroundColor: location.pathname === '/manage/processors' ? 'white' : 'transparent',
                    borderRadius: '6px',
                    padding: '0.5rem 0.75rem',
                    margin: '0.25rem 0',
                    fontWeight: location.pathname === '/manage/processors' ? '600' : '500',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none',
                    display: 'block',
                    cursor: 'pointer'
                  }}
                >
                   Processors
                </Link>

                <div className="dropdown-divider" style={{ borderColor: '#dee2e6' }}></div>

                <Link 
                  className={`collapse-item ${location.pathname === '/manage/managers' ? 'active' : ''}`} 
                  to="/manage/managers"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsManagementOpen(false);
                    if (location.pathname !== '/manage/managers') {
                      setTimeout(() => { navigate('/manage/managers'); }, 150);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== '/manage/managers') {
                      e.target.style.backgroundColor = '#e9ecef';
                      e.target.style.color = '#667eea';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== '/manage/managers') {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#495057';
                    }
                  }}
                  style={{
                    color: location.pathname === '/manage/managers' ? '#667eea' : '#495057',
                    backgroundColor: location.pathname === '/manage/managers' ? 'white' : 'transparent',
                    borderRadius: '6px',
                    padding: '0.5rem 0.75rem',
                    margin: '0.25rem 0',
                    fontWeight: location.pathname === '/manage/managers' ? '600' : '500',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none',
                    display: 'block',
                    cursor: 'pointer'
                  }}
                >
                  Managers
                </Link>
              </div>
            </div>
          </li>
        </>
      )}
      {/* Commissions Dropdown */}
          {permissionManager.hasPermission('view_dashboard') && (process.env.REACT_APP_API_BASE_URL !== 'https://backend.spireabstract.com/api') && (
            <>
              
              <li className={`nav-item ${isCommissionsRoute ? 'active' : ''}`}>
                <a 
                  className={`nav-link`} 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    toggleCommissionsDropdown();
                  }}
                  data-target="#collapseCommissions"
                  aria-expanded={isCommissionsOpen}
                  aria-controls="collapseCommissions"
                  style={{
                    color: isCommissionsRoute ? '#667eea' : '#495057',
                    backgroundColor: isCommissionsRoute ? '#f8f9fa' : 'transparent',
                    fontWeight: isCommissionsRoute ? '600' : '500',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <i  className="fas fa-fw fa-chart-line" style={{ marginRight: '0.5rem' }}></i>
                  <span>Reports &nbsp;</span>
                  <i className={`fas fa-chevron-down ms-auto transition-transform ${isCommissionsOpen ? 'rotate-180' : ''}`} style={{
                    transform: isCommissionsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease'
                  }}></i>
                </a>
                <div 
                  id="collapseCommissions" 
                  className={`collapse ${isCommissionsOpen ? 'show' : ''}`}
                  aria-labelledby="headingCommissions" 
                  data-parent="#accordionSidebar"
                >
                  <div className="py-2 collapse-inner rounded" style={{
                    borderLeft: '3px solid #e9ecef',
                    borderLeftStyle:"double"
                  }}>


                    <Link 
                      className={`collapse-item ${location.pathname === '/commissions' || location.pathname.startsWith('/commissions/') ? 'active' : ''}`} 
                      to="/commissions"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsCommissionsOpen(false);
                        if (location.pathname !== '/commissions') {
                          setTimeout(() => { navigate('/commissions'); }, 150);
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (location.pathname !== '/commissions') {
                          e.target.style.backgroundColor = '#e9ecef';
                          e.target.style.color = '#667eea';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (location.pathname !== '/commissions') {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#495057';
                        }
                      }}
                      style={{
                        color: location.pathname === '/commissions' || location.pathname.startsWith('/commissions/') ? '#667eea' : '#495057',
                        backgroundColor: location.pathname === '/commissions' || location.pathname.startsWith('/commissions/') ? 'white' : 'transparent',
                        borderRadius: '6px',
                        padding: '0.5rem 0.75rem',
                        margin: '0.25rem 0',
                        fontWeight: location.pathname === '/commissions' || location.pathname.startsWith('/commissions/') ? '600' : '500',
                        transition: 'all 0.3s ease',
                        textDecoration: 'none',
                        display: 'block',
                        cursor: 'pointer'
                      }}
                    >
                      Commission Report
                    </Link>
                                       <div className="dropdown-divider" style={{ borderColor: '#dee2e6' }}></div>

                    <Link 
                      className={`collapse-item ${location.pathname === '/salespersons' || location.pathname.startsWith('/salespersons/') ? 'active' : ''}`} 
                      to="/salespersons"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsCommissionsOpen(false);
                        if (location.pathname !== '/salespersons') {
                          setTimeout(() => { navigate('/salespersons'); }, 150);
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (location.pathname !== '/salespersons') {
                          e.target.style.backgroundColor = '#e9ecef';
                          e.target.style.color = '#667eea';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (location.pathname !== '/salespersons') {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#495057';
                        }
                      }}
                      style={{
                        color: location.pathname === '/salespersons' || location.pathname.startsWith('/salespersons/') ? '#667eea' : '#495057',
                        backgroundColor: location.pathname === '/salespersons' || location.pathname.startsWith('/salespersons/') ? 'white' : 'transparent',
                        borderRadius: '6px',
                        padding: '0.5rem 0.75rem',
                        margin: '0.25rem 0',
                        fontWeight: location.pathname === '/salespersons' || location.pathname.startsWith('/salespersons/') ? '600' : '500',
                        transition: 'all 0.3s ease',
                        textDecoration: 'none',
                        display: 'block',
                        cursor: 'pointer'
                      }}
                    >
                      Sales persons Commissions
                    </Link>


                  </div>
                </div>
              </li>
            </>
          )}
          

      {/* Settings Section - Only for super_admin */}
      {/* {permissionManager.hasPermission('manage_settings') && (
        <>
          <hr className="sidebar-divider" />
          <div className="sidebar-heading" style={{ color: 'white', fontWeight:"bold", fontSize:"0.7rem"}}>System</div>
          
          <li className={`nav-item ${location.pathname === '/settings' ? 'active' : ''}`}>
            <Link className="nav-link" to="/settings">
              <i className="fas fa-fw fa-cogs"></i>
              <span>Settings</span>
            </Link>
          </li>
          
          {permissionManager.hasPermission('view_logs') && (
             <li className={`nav-item ${location.pathname === '/logs' ? 'active' : ''}`}>
               <Link className="nav-link" to="/logs">
                 <i className="fas fa-fw fa-file-alt"></i>
                 <span>System Logs</span>
               </Link>
             </li>
           )}
         </>
       )} */}

      

      {/* Divider */}
      <hr className="sidebar-divider d-none d-md-block" style={{ borderColor: '#e9ecef' }} />

      {/* Sidebar Toggler (Sidebar) */}
      <div className="text-center d-none d-md-inline">
        <button className="rounded-circle border-0" style={{ position: 'absolute', bottom: '0%' }} id="sidebarToggle" ref={sidebarToggleRef}></button>
      </div>
    </ul>
    </>
  );
};

export default Sidebar;